import { BANDS, type BandId } from '@/domain/bands';
import type { Dictionary } from '@/i18n';

type BandLadderProps = {
  dict: Dictionary;
  band: BandId;
};

const ORDER: readonly BandId[] = ['excellent', 'covered', 'limited', 'far'];

const FILL = {
  excellent: 'bg-green-500',
  covered: 'bg-blue-500',
  limited: 'bg-yellow-500',
  far: 'bg-red-500',
  out_of_region: 'bg-grey-800',
} satisfies Record<BandId, string>;

const SEGMENT_LABEL = {
  excellent: `0–${BANDS.coveredFromKm}`,
  covered: `${BANDS.coveredFromKm}–${BANDS.limitedFromKm}`,
  limited: `${BANDS.limitedFromKm}–${BANDS.farFromKm}`,
  far: `${BANDS.farFromKm}+`,
  out_of_region: '',
} satisfies Record<BandId, string>;

const rangeText = (band: BandId, dict: Dictionary): string => {
  if (band === 'excellent') {
    return dict.ladder.upTo(BANDS.coveredFromKm);
  }
  if (band === 'covered') {
    return dict.ladder.between(BANDS.coveredFromKm, BANDS.limitedFromKm);
  }
  if (band === 'limited') {
    return dict.ladder.between(BANDS.limitedFromKm, BANDS.farFromKm);
  }
  if (band === 'far') {
    return dict.ladder.above(BANDS.farFromKm);
  }
  return dict.ladder.outOfRegion;
};

/* The ladder, not the kilometre reading, is the honest answer. It shows the four
   bands and where this lead sits, so the boundaries the whole company uses are
   visible instead of implied by a single number. */
export const BandLadder = ({ dict, band }: BandLadderProps) => {
  const activeIndex = ORDER.indexOf(band);

  return (
    <div
      role="img"
      aria-label={dict.ladder.ariaLabel(rangeText(band, dict))}
      className="flex gap-1.5"
    >
      {ORDER.map((segment, index) => {
        const isActive = index === activeIndex;
        return (
          <div key={segment} className="flex flex-1 flex-col gap-1.5">
            <span
              className={
                isActive
                  ? `animate-ladder-fill h-1.5 rounded-full ${FILL[band]}`
                  : 'h-1.5 rounded-full bg-grey-200'
              }
              aria-hidden="true"
            />
            <span
              aria-hidden="true"
              className={
                isActive
                  ? 'text-[11px] font-extrabold tabular-nums text-black'
                  : 'text-[11px] font-medium tabular-nums text-grey-600'
              }
            >
              {SEGMENT_LABEL[segment]} km
            </span>
          </div>
        );
      })}
    </div>
  );
};
